/**
 * Life4Earth V2 — ThemeProvider
 *
 * React context that manages the active theme and applies CSS custom
 * properties to :root at runtime.  Theme switches are instantaneous —
 * no page refresh required.
 *
 * Supports:
 *   - Built-in themes (dark, light)
 *   - Custom named themes (stored in themeRegistry localStorage)
 *   - Per-theme token overrides (themeOverrides)
 *   - Cross-tab synchronization via the browser `storage` event
 *   - RTL/LTR direction based on i18n language
 *
 * Usage:
 *   <ThemeProvider><App /></ThemeProvider>
 *
 *   const { themeName, setThemeName, availableThemes } = useTheme();
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { i18n } from '../i18n';
import { isRtlLanguage } from '../utils/rtl';
import { DARK_THEME } from './themes/dark';
import { LIGHT_THEME } from './themes/light';
import { resolveOverrideTokens, getTextureAllowlistRaw } from './themeOverrides';
import { getCustomTheme, getVisibleThemes, hydrateThemesFromApi } from './themeRegistry';
import {
  readTexturePreference, saveTexturePreference,
  applyBackgroundTexture, dispatchTextureChanged,
  isTextureCompatible,
  parseTextureAllowlist,
} from './backgroundTexture';
import type { TextureId, BaseTheme } from './backgroundTexture';
import type { ThemeContextType, ThemeTokens } from './types';
import { apiFetch, getCurrentUserGuid } from '../api/client';

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                    */
/* ------------------------------------------------------------------ */

const STORAGE_KEY = 'l4e_theme';
const BUILTIN_THEMES: Record<string, ThemeTokens> = {
  dark:  DARK_THEME,
  light: LIGHT_THEME,
};

function readSavedTheme(): string {
  try {
    const saved = window?.localStorage?.getItem(STORAGE_KEY);
    if (saved) return saved;
  } catch {
    // SSR or storage unavailable
  }
  return 'dark';
}

function persistTheme(name: string): void {
  try {
    window?.localStorage?.setItem(STORAGE_KEY, name);
  } catch {
    // ignore
  }
}

/** Resolve the base theme tokens for a given theme name. */
function resolveBaseTheme(name: string): ThemeTokens {
  // Built-in?
  if (BUILTIN_THEMES[name]) return BUILTIN_THEMES[name];

  // Custom theme → derive from its baseTheme
  const custom = getCustomTheme(name);
  if (custom) {
    return BUILTIN_THEMES[custom.baseTheme] || BUILTIN_THEMES['dark'];
  }

  // Fallback
  return BUILTIN_THEMES['dark'];
}

/** Write every token as a CSS custom property on :root. */
function applyTokensToRoot(tokens: ThemeTokens): void {
  const root = document.documentElement;
  const entries = Object.entries(tokens);
  for (let i = 0; i < entries.length; i++) {
    root.style.setProperty(entries[i][0], entries[i][1]);
  }
}

/** Set dir and lang attributes on <html> based on the current i18n language. */
function applyDirection(lang: string): void {
  const root = document.documentElement;
  const rtl = isRtlLanguage(lang);
  root.dir = rtl ? 'rtl' : 'ltr';
  root.lang = lang;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ThemeContext = createContext<ThemeContextType | null>(null);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeNameRaw] = useState<string>(readSavedTheme);
  const [textureId, setTextureIdRaw] = useState<TextureId>(readTexturePreference);

  /** Monotonic counter bumped when admin saves new overrides, to force re-merge. */
  const [overrideVersion, setOverrideVersion] = useState(0);

  // Hydrate custom themes from the API on mount.
  // This fetches all custom themes + overrides from the server and
  // writes them to localStorage, so other tabs / browsers see them.
  // The hydration call also fires l4e_theme_overrides_changed which
  // bumps overrideVersion to re-resolve tokens.
  useEffect(() => {
    hydrateThemesFromApi()
      .catch(() => {
        // API unavailable — fall back to localStorage cache
        // Still bump version so availableThemes reads from localStorage
        window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
      });
  }, []);

  // Hydrate background texture preference from the user's account on mount.
  // Falls back to localStorage if the API is unavailable or user is not authenticated.
  useEffect(() => {
    (async () => {
      try {
        const guid = getCurrentUserGuid();
        if (!guid) return; // not authenticated yet
        const data = await apiFetch<{ backgroundTexture?: string }>(
          `/api/security/users/${guid}/theme`,
        );
        const serverTexture = (data.backgroundTexture || 'solid') as TextureId;
        if (serverTexture !== textureId) {
          saveTexturePreference(serverTexture);
          setTextureIdRaw(serverTexture);
          // applyBackgroundTexture will be called by the textureId useEffect below
        }
      } catch {
        // API unavailable — keep localStorage / default value
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for same-tab theme-override changes.
  useEffect(() => {
    const handler = () => setOverrideVersion((n) => n + 1);
    window.addEventListener('l4e_theme_overrides_changed', handler);
    return () => window.removeEventListener('l4e_theme_overrides_changed', handler);
  }, []);

  // Listen for cross-tab localStorage changes.
  // The browser `storage` event fires on OTHER tabs when localStorage changes.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (
        e.key === 'l4e_theme_overrides' ||
        e.key === 'l4e_custom_themes' ||
        e.key === 'l4e_theme_visibility' ||
        e.key === 'l4e_theme_metadata'
      ) {
        // Theme data changed in another tab → re-resolve tokens
        setOverrideVersion((n) => n + 1);
      }
      if (e.key === 'l4e_theme') {
        // Active theme selection changed in another tab
        const newTheme = e.newValue;
        if (newTheme) setThemeNameRaw(newTheme);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const tokens = useMemo(() => {
    const base = resolveBaseTheme(themeName);
    const overrideTokens = resolveOverrideTokens(themeName);
    if (Object.keys(overrideTokens).length === 0) return base;
    return { ...base, ...overrideTokens };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName, overrideVersion]);

  // Apply tokens to :root whenever theme changes.
  useEffect(() => {
    applyTokensToRoot(tokens);
    // For data-theme, use the effective base (dark or light) for CSS selectors
    const custom = getCustomTheme(themeName);
    const dataTheme = custom ? custom.baseTheme : themeName;
    document.documentElement.setAttribute('data-theme', dataTheme);
  }, [tokens, themeName]);

  // Set initial direction and listen for i18n language changes.
  useEffect(() => {
    applyDirection(i18n.language || 'en');

    const onLanguageChanged = (lang: string) => {
      applyDirection(lang);
    };

    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  // Resolve effective base theme ('dark' or 'light') for texture compatibility.
  const effectiveBaseTheme: BaseTheme = useMemo(() => {
    const custom = getCustomTheme(themeName);
    return (custom ? custom.baseTheme : themeName === 'light' ? 'light' : 'dark') as BaseTheme;
  }, [themeName]);

  const setThemeName = useCallback((name: string) => {
    persistTheme(name);
    setThemeNameRaw(name);
  }, []);

  const setTextureId = useCallback((id: TextureId) => {
    saveTexturePreference(id);
    setTextureIdRaw(id);
    applyBackgroundTexture(id, effectiveBaseTheme);
    dispatchTextureChanged(id);

    // Persist to the server so the preference survives logout/login.
    // Fire-and-forget — localStorage is the immediate source of truth.
    (async () => {
      try {
        const guid = getCurrentUserGuid();
        if (!guid) return;
        // Fetch current theme so we don't clobber it
        const current = await apiFetch<{ themePreference?: string }>(
          `/api/security/users/${guid}/theme`,
        );
        await apiFetch(`/api/security/users/${guid}/theme`, {
          method: 'PUT',
          body: JSON.stringify({
            themePreference: current.themePreference || 'dark',
            backgroundTexture: id,
          }),
        });
      } catch {
        // API unavailable — localStorage already has it
      }
    })();
  }, [effectiveBaseTheme]);

  // Apply background texture on mount and when textureId or theme changes.
  // Uses a short delay on initial render to ensure .l4e-shell is in the DOM.
  useEffect(() => {
    // If the current texture is incompatible with the new theme, reset to solid.
    // DEV-1478: pass the theme NAME too — compatibility is now per built-in theme
    // (Hex Glow Black → Ninja, Blue → Dark), not merely per base. Without the
    // name this guard would still think both glows suit every dark theme.
    // DEV-1478: an admin may have configured which textures this theme offers.
    // Absent config → the code defaults apply.
    const allowlist = parseTextureAllowlist(getTextureAllowlistRaw(themeName));
    if (!isTextureCompatible(textureId, effectiveBaseTheme, themeName, allowlist)) {
      saveTexturePreference('solid');
      setTextureIdRaw('solid');
      applyBackgroundTexture('solid', effectiveBaseTheme);
      dispatchTextureChanged('solid');
      return;
    }
    // Clear the marker so the shell-mount observer knows this was an intentional
    // re-application (e.g. user picked a different texture) and won't skip it.
    const shell = document.querySelector('.l4e-shell') as HTMLElement | null;
    if (shell) delete shell.dataset.textureApplied;

    const timer = setTimeout(() => applyBackgroundTexture(textureId, effectiveBaseTheme), 50);
    return () => clearTimeout(timer);
    // DEV-1478: themeName is a dependency because the compatibility check above
    // now reads it. Ninja → Dark is a theme change with the SAME base, so
    // without this the guard would not re-run and an incompatible texture would
    // survive the switch.
  }, [textureId, effectiveBaseTheme, themeName]);

  // Re-apply texture whenever .l4e-shell is (re-)mounted in the DOM.
  // This handles logout/login cycles: the shell unmounts on logout
  // (losing its inline styles) and remounts on login as a fresh element.
  // Since textureId and effectiveBaseTheme don't change across that
  // transition, the effect above won't re-fire, so this observer
  // catches the new element and re-applies the texture.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const shell = document.querySelector('.l4e-shell') as HTMLElement | null;
      if (shell && !shell.dataset.textureApplied) {
        shell.dataset.textureApplied = '1';
        applyBackgroundTexture(textureId, effectiveBaseTheme);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [textureId, effectiveBaseTheme]);

  // Cross-tab sync for texture preference
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'l4e_bg_texture' && e.newValue) {
        const id = e.newValue as TextureId;
        setTextureIdRaw(id);
        applyBackgroundTexture(id, effectiveBaseTheme);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [effectiveBaseTheme]);

  const toggleTheme = useCallback(() => {
    // Toggle between dark and light. If on a custom theme, go to its opposite base.
    const custom = getCustomTheme(themeName);
    const effectiveBase = custom ? custom.baseTheme : themeName;
    setThemeName(effectiveBase === 'dark' ? 'light' : 'dark');
  }, [themeName, setThemeName]);

  /** List of themes visible in the user profile selector. */
  const availableThemes = useMemo(() => {
    // Re-compute when overrideVersion changes (visibility may have changed)
    return getVisibleThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideVersion]);

  const value: ThemeContextType = useMemo(
    () => ({ themeName, setThemeName, toggleTheme, tokens, availableThemes, textureId, setTextureId }),
    [themeName, setThemeName, toggleTheme, tokens, availableThemes, textureId, setTextureId],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used inside <ThemeProvider>.');
  }
  return ctx;
}

/**
 * DEV-1473. Is the EFFECTIVE base theme light? Custom themes resolve to the base
 * they were built on, so a custom light theme answers true — the same resolution
 * the `data-theme` attribute uses, so CSS and JS never disagree.
 *
 * Prefer CSS (`:global(html[data-theme='light'])`) whenever the rule can live in
 * a stylesheet. This hook is for the cases that genuinely cannot — an inline
 * style object, or a decision about whether to render an element at all.
 */
export function useIsLightTheme(): boolean {
  const { themeName } = useTheme();
  return useMemo(() => {
    const custom = getCustomTheme(themeName);
    return (custom ? custom.baseTheme : themeName) === 'light';
  }, [themeName]);
}
